/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { useDispatch } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchExpiringUrl } from "components/EnhancedSpeech/Utilities";
import { addToast } from "actions/ToastMessageActions";
import { useResettableTimeout } from "hooks/useResettableTimeout";

export const useExpiringUrl = ({
  refreshUrl,
  refreshIntervalSeconds = 290, // Default to 5 minutes (minus 10 seconds) because S3 signed URLS have a lifespan floor of 5 minutes due to being tied to per-instance session tokens that are refreshed 5 minutes before expiry
}: {
  refreshUrl: string;
  refreshIntervalSeconds?: number;
}): string | null => {
  const dispatch = useDispatch();
  const { resetTimeout, clearOutstandingTimeout } = useResettableTimeout();
  const [expiringUrl, setExpiringUrl] = useState<string | null>(null);
  const unmounted = useRef(false);

  const refreshExpiringUrl = useCallback(async (): Promise<void> => {
    if (unmounted.current || !refreshUrl) {
      return;
    }

    fetchExpiringUrl(refreshUrl).then((url) => {
      if (unmounted.current) {
        return;
      }

      if (!url) {
        // fetchExpiringUrl returns empty string if it fails
        dispatch(
          addToast({ message: I18n.t("enhance_speech.loading_track_failed") })
        );

        return;
      }

      setExpiringUrl(url);
    });
  }, [dispatch, refreshUrl, setExpiringUrl]);

  // Clear timeout on unmount
  useEffect(() => {
    unmounted.current = false;

    return (): void => {
      clearOutstandingTimeout();
      unmounted.current = true;
    };
  }, [clearOutstandingTimeout]);

  // When refresh url changes, clear the current expiring url, clear the outstanding timeout,
  // and refresh immediately
  useEffect(() => {
    clearOutstandingTimeout();
    setExpiringUrl(null);

    if (refreshUrl) {
      refreshExpiringUrl();
    }
  }, [
    dispatch,
    refreshExpiringUrl,
    resetTimeout,
    refreshUrl,
    clearOutstandingTimeout,
  ]);

  // Once refresh is complete (expiringUrl updated), set timer to refresh
  // again before it expires
  useEffect(() => {
    if (!expiringUrl) {
      return;
    }

    resetTimeout(refreshExpiringUrl, refreshIntervalSeconds * 1000);
  }, [refreshExpiringUrl, expiringUrl, refreshIntervalSeconds, resetTimeout]);

  return expiringUrl;
};
